import { Conversions } from "helpers";
import { RuleType } from "types";

/**
 * function to join classNames
 * @param {any} args classes
 * @return {string} merged array of classes
 */

export const classNames = (...args: any): string => {
  return args.filter((x: any) => !!x).join(" ");
}

/**
 * function to change a position in the array (for state)
 * @param {any[]} arr array to be modified
 * @param {number} index index to be updated
 * @param {any} value value to be updated
 * @return {any[]} merged array of classes
 */

export const setArrayIndex = (arr: any[], index: number, value: any) => {
  const items = [...arr];
  items[index] = value;
  return items;
}

/**
 * function to add leading zeros
 * @param {number} value number to be filled with leading zeros
 * @param {number} size result length (default 2)
 * @return {string} value if the leading zeros
 */

export const pad = (value: number | any, size: number = 2): string => {
  return !isNaN(Number.parseFloat(value))
    ? "0".repeat(Math.max(0, size - Math.abs(value).toString().length)) +
    Math.abs(value)
    : "";
};

/**
 * function to generate an array of a given size
 * @param {number} count size of the array 
 * @return {array} array of numbers
 */

export const generateArray = (count: number): Array<number> => {
  return Array.from(Array(count).keys());
}

/**
 * function to remove all properties that contains null or undefined values
 * @param {Object} obj the object to be modified
 * @return {Object} the new object with all properties removed
 */

export const removeFromObject = (obj: any): Object => {
  const cpy = { ...obj };
  Object.keys(cpy).forEach((key: string) => {
    if (cpy[key] === null || cpy[key].toString().length === 0 || cpy[key] === 0) {
      delete cpy[key];
    }
  });
  return cpy;
}

/**
 * it simulates a asynchronous call to an API function
 * @param {Function} callback the function to be called
 */

export const asyncMethod = async (callback: Function) => {
  const delayPromise = (ms: number) => new Promise(res => setTimeout(res, ms))
  await delayPromise(500)
  return callback()
}

/**
 * get enum value by key
 * @param {enum} enumerator enum to be used
 * @param {string} key key to be searched 
 * @return 
 */

export const getEnumByKey = (enumerator: any, key: string) => {
  const indexOf = Object.values(enumerator).indexOf(key as unknown);
  return indexOf !== -1 ? Object.keys(enumerator)[indexOf] : null;
}

/**
 * it deeply merges two objects (only given properties are replaced)
 * @param {object} source the source object 
 * @param {object} replacement the object to be merged with the source
 * @return {object} the merged object
 */

export const deepInterpolation = (source: any, replacement: any): any => {
  // Create a copy of the original object to avoid modifying the original data
  const result = { ...source };

  // Recursively merge the replacement object into the original object
  const mergeObjects = (obj1: any, obj2: any) => {
    for (const key in obj2) {
      if (obj2.hasOwnProperty(key)) {
        // Check whether the property is an object for doing deep merging (if it is array, it must replace it)
        if (obj2[key] instanceof Object && obj1.hasOwnProperty(key) && obj1[key] instanceof Object && !Array.isArray(obj1[key])) {
          mergeObjects(obj1[key], obj2[key]);
        } else {
          obj1[key] = obj2[key];
        }
      }
    }
  };

  mergeObjects(result, replacement);

  return result;
}

/**
 * function to get the name of the timezone
 * @return {string} the time zone name
 */

export const getTimeZoneDescription = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * function to get the time offset of a given date (or current date if nothing is given)
 * @param {Date | null} date the date where the time offset should be retrieved
 * @returns {string} time offset
 */

export const getTimeZoneOffset = (date?: Date | null): string => {
  const offset = (date ?? new Date()).getTimezoneOffset();
  const behind = offset > 0;
  return `${behind ? "-" : ""}${Conversions.toTimeString(offset)}`;
};

/**
 * function to add time zone offset to a given date
 * @param {Date} date date which the offset should be added
 * @return {Date} the new date
 */

export const addTimeZoneOffset = (date?: Date | null): Date | null => {
  if (!date) return null;
  const timeZoneOffset = date.getTimezoneOffset();
  const minutes = timeZoneOffset < 0 ? -timeZoneOffset : timeZoneOffset;
  date.setMinutes(date.getMinutes() + minutes);
  return date;
}

/**
 * method that returns the propert text color according to the provided background color based on the luminance
 * @param {string} backgroundColor the background to be used as reference
 * @param {string} black the color to be used in case its black
 * @param {string} white the color to be used in case its white 
 * @return {string} the contrast color
 */

export const getContrast = (backgroundColor: string, black: string = "black", white: string = "white"): string => {
  // Helper function to calculate the luminance of an RGB color
  const getLuminance = ([r, g, b]: [number, number, number]): number => {
    const [rl, gl, bl] = [r / 255, g / 255, b / 255];
    const [rSrgb, gSrgb, bSrgb] = [
      rl <= 0.03928 ? rl / 12.92 : ((rl + 0.055) / 1.055) ** 2.4,
      gl <= 0.03928 ? gl / 12.92 : ((gl + 0.055) / 1.055) ** 2.4,
      bl <= 0.03928 ? bl / 12.92 : ((bl + 0.055) / 1.055) ** 2.4,
    ];
    return 0.2126 * rSrgb + 0.7152 * gSrgb + 0.0722 * bSrgb;
  };

  // Convert the background color to RGB format
  let rgbColor: [number, number, number];
  if (backgroundColor.startsWith("#")) {
    rgbColor = Conversions.toRgbAsHex(backgroundColor);
  } else if (backgroundColor.startsWith("rgb(")) {
    rgbColor = Conversions.toRgbAsString(backgroundColor);
  } else {
    throw new Error("Invalid background color format. Please provide a color in hex or RGB format.");
  }

  // Calculate the luminance of the background color
  const luminance = getLuminance(rgbColor);

  // Determine the recommended text color based on luminance
  return luminance > 0.5 ? black : white;
}

/**
 * function to return the respective greeting to the user according to the current time
 * @return {string} the greeting message
 */

export const greeting = (): string => {
  const hour = new Date().getHours();
  return hour < 12 ? "good_morning" : hour > 12 && hour < 18 ? "good_afternoon" : hour > 22 ? "hello" : "good_evening";
};

/**
 * function to find object based on it
 * @param {T[]} array of objects 
 * @param {string} target ID
 * @returns {T} object based on ID
 */

export const get = <T>(array: T[], target: string): T => {
  return array.find((item: T) => (item as any)._id === target) as T;
}

/**
 * function to generate ObjectId
 * @returns {string} the string object id
 */

export const generateObjectId = (): string => {
  const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, "0");

  const randomBytes = Array.from({ length: 5 }, () =>
    Math.floor(Math.random() * 256)
  );

  const counterBytes = Array.from({ length: 3 }, () =>
    Math.floor(Math.random() * 256)
  );

  const toHex = (bytes: number[]) =>
    bytes.map((b) => b.toString(16).padStart(2, "0")).join("");

  return timestamp + toHex(randomBytes) + toHex(counterBytes);
};

/**
 * Walks through an object and applies a set of rules to modify its values.
 *
 * The rules are an array of objects with a `path` and a `value` property.
 * The `path` is a string with the path to the value to be modified, using
 * dot notation (e.g. `a.b.c`). The `value` can be either a primitive value
 * or a function that takes the current value and returns a new value.
 *
 * If the `path` points to an array, the rule is applied to all items in
 * the array.
 *
 * The function returns a new object with the modified values, without
 * modifying the original object.
 *
 * @param obj the object to walk and modify
 * @param rules an array of rules to apply
 * @returns the modified object
 */

export const walkAndReplace = <T>(obj: T, rules: RuleType[]): T => {
  const updated = structuredClone(obj); // safe deep clone

  for (const rule of rules) {
    const pathSegments = rule.path.split(".");

    const apply = (target: any, segments: string[]) => {
      const key = segments[0];
      if (!target) return;

      // Handle arrays (loop through)
      if (Array.isArray(target)) {
        target.forEach((item) => apply(item, segments));
        return;
      }

      // Base case: last segment
      if (segments.length === 1) {
        const current = target[key];
        const shouldOverride = rule.override !== false || Object.keys(current).length === 0 || current === undefined || current === null || current === "";

        if (shouldOverride) {
          if (typeof rule.value === "function") {
            target[key] = rule.value(current);
          } else if (Array.isArray(current)) {
            // Append single item to existing array
            target[key] = Array.isArray(rule.value) ? [...current, ...rule.value] : [...current, rule.value];
          } else {
            // Replace in all other cases
            target[key] = rule.value;
          }
        }
      } else {
        apply(target[key], segments.slice(1));
      }
    };

    apply(updated, pathSegments);
  }

  return updated;
};
